import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
// Context for managing popover state
interface PopoverContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const PopoverContext = createContext<PopoverContextValue | null>(null);

// Main popover container
export interface PopoverProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const Popover: React.FC<PopoverProps> = ({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
}) => {
  const [_, setInternalOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isControlled = open !== undefined;
  const openValue = isControlled ? open : internalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  // Close popover when clicking outside
  useEffect(() => {
    if (!openValue) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        const popoverContent = document.querySelector('[data-popover-content]');
        if (popoverContent && !popoverContent.contains(event.target as Node)) {
          handleOpenChange(false);
        }
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openValue]);

  return (
    <PopoverContext.Provider
      value={{ open: openValue, onOpenChange: handleOpenChange, triggerRef }}
    >
      <div className="relative inline-block">{children}</div>
    </PopoverContext.Provider>
  );
};

// Popover trigger button
export interface PopoverTriggerProps {
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

export const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  ({ children, className, asChild, ...props }, _ref) => {
    const context = useContext(PopoverContext);

    if (!context) {
      throw new Error('PopoverTrigger must be used within a Popover');
    }

    const handleClick = () => {
      context.onOpenChange(!context.open);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...children.props,
        ref: context.triggerRef,
        onClick: handleClick,
        'aria-expanded': context.open,
        'aria-haspopup': 'dialog',
      });
    }

    return (
      <button
        ref={context.triggerRef}
        onClick={handleClick}
        aria-expanded={context.open}
        aria-haspopup="dialog"
        className={cn(
          'inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium',
          'ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          'hover:bg-gray-100 hover:text-gray-900',
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

PopoverTrigger.displayName = 'PopoverTrigger';

// Popover content container
export interface PopoverContentProps {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  alignOffset?: number;
  collisionPadding?: number;
}

export const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      children,
      className,
      align = 'center',
      side = 'bottom',
      sideOffset = 4,
      alignOffset = 0,
      collisionPadding: _collisionPadding = 8,

      ...props
    },
    ref
  ) => {
    const context = useContext(PopoverContext);

    if (!context) {
      throw new Error('PopoverContent must be used within a Popover');
    }

    // Calculate position based on trigger element
    const [_, setPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (!context.triggerRef.current) return;

      const triggerRect = context.triggerRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (side) {
        case 'top':
          top = triggerRect.top - sideOffset;
          break;
        case 'bottom':
          top = triggerRect.bottom + sideOffset;
          break;
        case 'left':
          left = triggerRect.left - sideOffset;
          top = triggerRect.top;
          break;
        case 'right':
          left = triggerRect.right + sideOffset;
          top = triggerRect.top;
          break;
      }

      switch (align) {
        case 'start':
          if (side === 'top' || side === 'bottom') {
            left = triggerRect.left + alignOffset;
          }
          break;
        case 'center':
          if (side === 'top' || side === 'bottom') {
            left = triggerRect.left + triggerRect.width / 2 + alignOffset;
          }
          break;
        case 'end':
          if (side === 'top' || side === 'bottom') {
            left = triggerRect.right - alignOffset;
          }
          break;
      }

      setPosition({ top, left });
    }, [context.open, context.triggerRef, side, align, sideOffset, alignOffset]);

    if (!context.open) return null;

    return (
      <>
        {/* Backdrop/overlay */}
        <div className="fixed inset-0 z-40" onClick={() => context.onOpenChange(false)} />

        {/* Content */}
        <div
          ref={ref}
          data-popover-content
          role="dialog"
          className={cn(
            'fixed z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white p-4 shadow-lg',
            'animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2',
            'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
            'data-[side=top]:slide-in-from-bottom-2',
            className
          )}
          style={{
            position: 'fixed',
            top: position.top,
            left: align === 'center' ? position.left - 64 : position.left, // Approximate adjustment for center alignment
          }}
          {...props}
        >
          {children}
        </div>
      </>
    );
  }
);

PopoverContent.displayName = 'PopoverContent';
